// Batch 5 of the Compare tab content (English and Italian).
// Same structure as the other batches.

export const BATCH_5 = [
    {
        id: 'adverbs',
        level: 'A2',
        title: { en: 'Adverbs and how often', it: 'Avverbi e frequenza' },
        summary: {
            en: 'How, how often and how much something happens.',
            it: 'Come, quanto spesso e quanto succede qualcosa.',
        },
        forms: {
            english: [
                {
                    name: 'Adjective + -ly',
                    formula: {
                        en: 'Most adverbs add -ly to the adjective: slow, slowly. Irregular: good, well. Frequency adverbs (always, often, sometimes, never) go before the main verb: I always work.',
                        it: 'La maggior parte degli avverbi aggiunge -ly all\'aggettivo: slow, slowly. Irregolare: good, well. Gli avverbi di frequenza (always, often, sometimes, never) vanno prima del verbo principale: I always work.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Aggettivo + -mente',
                    formula: {
                        en: 'Add -mente to the feminine form of the adjective: lento, lentamente. Adjectives in -e just add it: facile, facilmente. Adjectives in -le or -re drop the e: regolare, regolarmente. Irregular: buono, bene. Frequency adverbs: sempre, spesso, a volte, mai, di solito.',
                        it: 'Aggiungi -mente alla forma femminile dell\'aggettivo: lento, lentamente. Gli aggettivi in -e lo aggiungono direttamente: facile, facilmente. Quelli in -le o -re perdono la e: regolare, regolarmente. Irregolare: buono, bene. Avverbi di frequenza: sempre, spesso, a volte, mai, di solito.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'She speaks slowly.', tag: 'Adjective + -ly' },
                italian: { text: 'Parla lentamente.', tag: 'Adjective + -mente' },
            },
            {
                english: { text: 'He works well.', tag: 'well (irregular)' },
                italian: { text: 'Lavora bene.', tag: 'bene (irregular)' },
            },
            {
                english: { text: 'I always work on Saturdays.', tag: 'always before the verb' },
                italian: { text: 'Lavoro sempre il sabato.', tag: 'sempre after the verb' },
            },
            {
                english: { text: 'She often goes to the gym.', tag: 'often before the verb' },
                italian: { text: 'Va spesso in palestra.', tag: 'spesso after the verb' },
            },
            {
                english: { text: 'We have never been to Naples.', tag: 'never' },
                italian: { text: 'Non siamo mai stati a Napoli.', tag: 'non ... mai' },
            },
            {
                english: { text: "Unfortunately, he can't come.", tag: 'Sentence adverb' },
                italian: { text: 'Purtroppo non può venire.', tag: 'Sentence adverb' },
            },
        ],
        notes: [
            {
                en: "Frequency adverbs go before the verb in English (I always work) and usually after it in Italian (Lavoro sempre). In compound tenses they sit between the auxiliary and the participle: 'Ho sempre lavorato'.",
                it: "Gli avverbi di frequenza vanno prima del verbo in inglese (I always work) e di solito dopo in italiano (Lavoro sempre). Nei tempi composti stanno tra l'ausiliare e il participio: 'Ho sempre lavorato'.",
            },
            {
                en: "Italian uses 'non ... mai' (a double negative) where English has a single negative word: 'Non siamo mai stati a Napoli'.",
                it: "L'italiano usa 'non ... mai' (doppia negazione) dove l'inglese ha una sola parola negativa: 'Non siamo mai stati a Napoli'.",
            },
            {
                en: "Not every Italian adverb ends in -mente. Many are fixed phrases: di solito (usually), a volte (sometimes), in fretta (quickly), di corsa (running).",
                it: "Non tutti gli avverbi italiani finiscono in -mente. Molti sono espressioni fisse: di solito, a volte, in fretta, di corsa.",
            },
        ],
    },
    {
        id: 'time-expressions',
        level: 'A2',
        title: { en: 'Ago, for, since, in', it: 'Fa, per, da, tra' },
        summary: {
            en: 'Saying when something happened and how long it lasted.',
            it: 'Dire quando è successo qualcosa e quanto è durato.',
        },
        forms: {
            english: [
                {
                    name: 'Ago / for / since / in',
                    formula: {
                        en: 'ago after a time (two years ago). for + length of time (for two years). since + starting point (since 2020). in + time from now (in ten minutes).',
                        it: 'ago dopo un periodo (two years ago). for + durata (for two years). since + punto di partenza (since 2020). in + tempo da adesso (in ten minutes).',
                    },
                },
            ],
            italian: [
                {
                    name: 'Fa / per / da / tra',
                    formula: {
                        en: 'fa after a time (due anni fa). per for a finished period (per due anni). da + presente for a situation that continues (da due anni, dal 2020). tra or fra for time from now (tra dieci minuti).',
                        it: 'fa dopo un periodo (due anni fa). per per un periodo concluso (per due anni). da + presente per una situazione che continua (da due anni, dal 2020). tra o fra per il tempo da adesso (tra dieci minuti).',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'We moved here two years ago.', tag: 'ago' },
                italian: { text: 'Ci siamo trasferiti qui due anni fa.', tag: 'fa' },
            },
            {
                english: { text: 'I worked there for two years.', tag: 'for (finished)' },
                italian: { text: 'Ho lavorato lì per due anni.', tag: 'per' },
            },
            {
                english: { text: 'I have worked here for two years.', tag: 'for (continuing)' },
                italian: { text: 'Lavoro qui da due anni.', tag: 'da + presente' },
            },
            {
                english: { text: 'I have lived here since 2020.', tag: 'since' },
                italian: { text: 'Vivo qui dal 2020.', tag: 'da + il = dal' },
            },
            {
                english: { text: 'The train leaves in ten minutes.', tag: 'in (from now)' },
                italian: { text: 'Il treno parte tra dieci minuti.', tag: 'tra' },
            },
            {
                english: { text: 'I read during the flight.', tag: 'during' },
                italian: { text: 'Ho letto durante il volo.', tag: 'durante' },
            },
        ],
        notes: [
            {
                en: "English uses 'for' for both finished and continuing periods. Italian uses per for a finished one (Ho lavorato lì per due anni) and da + presente for one that continues (Lavoro qui da due anni).",
                it: "L'inglese usa 'for' sia per i periodi conclusi sia per quelli che continuano. L'italiano usa per per uno concluso (Ho lavorato lì per due anni) e da + presente per uno che continua (Lavoro qui da due anni).",
            },
            {
                en: "'Ago' goes after the time in English, and so does 'fa' in Italian: 'due anni fa'.",
                it: "'Ago' va dopo il periodo in inglese, e così 'fa' in italiano: 'due anni fa'.",
            },
            {
                en: "For how long something takes, Italian uses in: 'L'ho fatto in due ore' (I did it in two hours). For time from now, use tra or fra: 'tra due ore' (in two hours' time).",
                it: "Per la durata di un'azione, l'italiano usa in: 'L'ho fatto in due ore'. Per il tempo da adesso, usa tra o fra: 'tra due ore'.",
            },
        ],
    },
    {
        id: 'already-yet',
        level: 'A2',
        title: { en: 'Already, yet, still, anymore', it: 'Già, ancora, non più' },
        summary: {
            en: 'Small words about whether something has happened or is still happening.',
            it: 'Piccole parole per dire se qualcosa è già successo o succede ancora.',
        },
        forms: {
            english: [
                {
                    name: 'Already / yet / still / anymore',
                    formula: {
                        en: "already: it has happened. yet: not so far (negatives and questions). still: it continues. not ... anymore: it stopped. They have different positions: I have already eaten, I haven't eaten yet.",
                        it: "already: è già successo. yet: finora no (negazioni e domande). still: continua. not ... anymore: si è interrotto. Hanno posizioni diverse: I have already eaten, I haven't eaten yet.",
                    },
                },
            ],
            italian: [
                {
                    name: 'Già / ancora / non ... più',
                    formula: {
                        en: 'già = already. ancora = still, and also yet in a negative sentence (non ... ancora). non ... più = not anymore. In compound tenses they go between the auxiliary and the participle: Ho già mangiato.',
                        it: 'già = already. ancora = still, e anche yet in una frase negativa (non ... ancora). non ... più = anymore. Nei tempi composti vanno tra l\'ausiliare e il participio: Ho già mangiato.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I have already eaten.', tag: 'already' },
                italian: { text: 'Ho già mangiato.', tag: 'già' },
            },
            {
                english: { text: "I haven't eaten yet.", tag: 'yet' },
                italian: { text: 'Non ho ancora mangiato.', tag: 'non ... ancora' },
            },
            {
                english: { text: 'Are you still at work?', tag: 'still' },
                italian: { text: 'Sei ancora al lavoro?', tag: 'ancora' },
            },
            {
                english: { text: "I don't work there anymore.", tag: 'not ... anymore' },
                italian: { text: 'Non lavoro più lì.', tag: 'non ... più' },
            },
            {
                english: { text: 'Have you finished already?', tag: 'already (question)' },
                italian: { text: 'Hai già finito?', tag: 'già' },
            },
        ],
        notes: [
            {
                en: "Ancora does two jobs. It means 'still' ('Sei ancora al lavoro?') and, with non, 'yet' ('Non ho ancora mangiato'). English has two different words.",
                it: "Ancora fa due lavori. Vuol dire 'still' ('Sei ancora al lavoro?') e, con non, 'yet' ('Non ho ancora mangiato'). L'inglese ha due parole diverse.",
            },
            {
                en: "'Non ... più' means 'no longer' or 'not anymore' (Non lavoro più lì). Do not confuse it with 'più' on its own, which means 'more'.",
                it: "'Non ... più' vuol dire 'no longer' o 'not anymore' (Non lavoro più lì). Non confonderlo con 'più' da solo, che vuol dire 'more'.",
            },
        ],
    },
    {
        id: 'too-enough',
        level: 'A2',
        title: { en: 'Too, enough, so', it: 'Troppo, abbastanza, così' },
        summary: {
            en: 'More than needed, as much as needed, and emphasis.',
            it: 'Più del necessario, quanto basta, ed enfasi.',
        },
        forms: {
            english: [
                {
                    name: 'Too / enough / so / too (also)',
                    formula: {
                        en: 'too + adjective (too hot). adjective + enough (hot enough), but enough + noun (enough time). so + adjective for emphasis (so beautiful). too also means as well: me too.',
                        it: 'too + aggettivo (too hot). aggettivo + enough (hot enough), ma enough + nome (enough time). so + aggettivo per enfasi (so beautiful). too vuol dire anche: me too.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Troppo / abbastanza / così / anche',
                    formula: {
                        en: 'troppo + adjective (troppo caldo). abbastanza goes before the adjective or noun (abbastanza caldo, abbastanza tempo). così = so. anche = also, too.',
                        it: 'troppo + aggettivo (troppo caldo). abbastanza va prima dell\'aggettivo o del nome (abbastanza caldo, abbastanza tempo). così = so. anche = also, too.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'It is too hot.', tag: 'too + adjective' },
                italian: { text: 'Fa troppo caldo.', tag: 'troppo' },
            },
            {
                english: { text: 'She is tired too.', tag: 'too (also)' },
                italian: { text: 'Anche lei è stanca.', tag: 'anche' },
            },
            {
                english: { text: 'The soup is not hot enough.', tag: 'adjective + enough' },
                italian: { text: 'La zuppa non è abbastanza calda.', tag: 'abbastanza + adjective' },
            },
            {
                english: { text: 'I have enough time.', tag: 'enough + noun' },
                italian: { text: 'Ho abbastanza tempo.', tag: 'abbastanza + noun' },
            },
            {
                english: { text: 'It is so beautiful!', tag: 'so + adjective' },
                italian: { text: 'È così bello!', tag: 'così' },
            },
            {
                english: { text: "That's enough!", tag: 'enough (stop)' },
                italian: { text: 'Basta!', tag: 'Basta' },
            },
        ],
        notes: [
            {
                en: "English 'too' has two meanings (too much, and also). Italian uses two different words: troppo (too much) and anche (also).",
                it: "L'inglese 'too' ha due significati (troppo, e anche). L'italiano usa due parole diverse: troppo e anche.",
            },
            {
                en: "'Enough' goes after the adjective in English (hot enough). 'Abbastanza' goes before it in Italian (abbastanza caldo).",
                it: "'Enough' va dopo l'aggettivo in inglese (hot enough). 'Abbastanza' va prima in italiano (abbastanza caldo).",
            },
        ],
    },
    {
        id: 'phrasal-verbs',
        level: 'B1',
        title: { en: 'Phrasal verbs (give up, look for)', it: 'Verbi con particella (give up, look for)' },
        summary: {
            en: 'English verb + small word, and the single Italian verbs that match them.',
            it: 'Verbo inglese + particella, e i verbi italiani singoli che corrispondono.',
        },
        forms: {
            english: [
                {
                    name: 'Verb + particle',
                    formula: {
                        en: 'A verb plus a small word (up, off, out, for, back) makes a new meaning: look, look for, look after, look up. Direction is often shown by the particle: run into the house.',
                        it: 'Un verbo più una particella (up, off, out, for, back) crea un significato nuovo: look, look for, look after, look up. La direzione è spesso data dalla particella: run into the house.',
                    },
                },
            ],
            italian: [
                {
                    name: 'A different single verb',
                    formula: {
                        en: 'Italian usually has one separate verb instead: cercare (look for), spegnere (turn off), scoprire (find out), tornare (come back), uscire (go out). Direction is in the verb (entrare, uscire, salire, scendere), and the manner is added separately: di corsa.',
                        it: 'L\'italiano di solito ha un verbo singolo diverso: cercare, spegnere, scoprire, tornare, uscire. La direzione è nel verbo (entrare, uscire, salire, scendere) e il modo si aggiunge a parte: di corsa.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I am looking for my keys.', tag: 'look for' },
                italian: { text: 'Cerco le mie chiavi.', tag: 'cercare (no preposition)' },
            },
            {
                english: { text: 'Turn off the light.', tag: 'turn off' },
                italian: { text: 'Spegni la luce.', tag: 'spegnere' },
            },
            {
                english: { text: 'She gave up smoking.', tag: 'give up' },
                italian: { text: 'Ha smesso di fumare.', tag: 'smettere di' },
            },
            {
                english: { text: 'We found out the truth.', tag: 'find out' },
                italian: { text: 'Abbiamo scoperto la verità.', tag: 'scoprire' },
            },
            {
                english: { text: 'Come back soon!', tag: 'come back' },
                italian: { text: 'Torna presto!', tag: 'tornare' },
            },
            {
                english: { text: 'He ran into the house.', tag: 'ran + into' },
                italian: { text: 'È entrato in casa di corsa.', tag: 'entrare + di corsa' },
            },
        ],
        notes: [
            {
                en: "Do not translate a phrasal verb word by word. 'Look for' is not 'guardare per': it is 'cercare', with no preposition.",
                it: "Non tradurre un phrasal verb parola per parola. 'Look for' non è 'guardare per': è 'cercare', senza preposizione.",
            },
            {
                en: "English shows direction with a particle (ran into the house). Italian shows it in the verb (è entrato) and adds the manner after (di corsa). This is why many English motion verbs need two Italian words.",
                it: "L'inglese mostra la direzione con una particella (ran into the house). L'italiano la mostra nel verbo (è entrato) e aggiunge il modo dopo (di corsa). Per questo molti verbi di moto inglesi in italiano richiedono due parole.",
            },
            {
                en: 'Learn the Italian verb as a new word, not as a translation of the particle. One English verb can have several Italian matches depending on the particle: give up (smettere), give back (restituire), give in (arrendersi).',
                it: 'Impara il verbo italiano come una parola nuova, non come traduzione della particella. Un verbo inglese può avere più corrispondenti italiani a seconda della particella: give up (smettere), give back (restituire), give in (arrendersi).',
            },
        ],
    },
    {
        id: 'deduction',
        level: 'B1',
        title: { en: 'Guessing: must be, might, can\'t be', it: 'Fare ipotesi: deve essere, potrebbe, non può essere' },
        summary: {
            en: 'How sure you are about something you cannot check.',
            it: 'Quanto sei sicuro di qualcosa che non puoi verificare.',
        },
        forms: {
            english: [
                {
                    name: 'Must / might / can\'t',
                    formula: {
                        en: 'must be = almost certain. might, may, could be = possible. can\'t be = almost impossible. For the past: must have + participle, might have + participle.',
                        it: 'must be = quasi certo. might, may, could be = possibile. can\'t be = quasi impossibile. Per il passato: must have + participio, might have + participio.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Dovere, potere, forse, futuro',
                    formula: {
                        en: 'dovere + essere = almost certain: Deve essere stanco. potere / forse / può darsi che = possible: Potrebbe venire. Non può essere = impossible. The futuro also guesses: Sarà a casa. The futuro anteriore guesses about the past: Saranno partiti.',
                        it: 'dovere + essere = quasi certo: Deve essere stanco. potere / forse / può darsi che = possibile: Potrebbe venire. Non può essere = impossibile. Il futuro fa ipotesi: Sarà a casa. Il futuro anteriore fa ipotesi sul passato: Saranno partiti.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'He must be tired.', tag: 'must be' },
                italian: { text: 'Deve essere stanco.', tag: 'dovere + essere' },
            },
            {
                english: { text: 'She might come.', tag: 'might' },
                italian: { text: 'Potrebbe venire.', tag: 'potere (condizionale)' },
            },
            {
                english: { text: "It can't be true.", tag: "can't be" },
                italian: { text: 'Non può essere vero.', tag: 'non potere + essere' },
            },
            {
                english: { text: 'He may be at home.', tag: 'may be' },
                italian: { text: 'Forse è a casa.', tag: 'forse + indicative' },
            },
            {
                english: { text: 'They must have left.', tag: 'must have + participle' },
                italian: { text: 'Devono essere partiti.', tag: 'dovere + essere + participle' },
            },
        ],
        notes: [
            {
                en: "English has a ladder of modal verbs for certainty (must, may, might, can't). Italian uses dovere for 'almost sure', potere or 'forse' for 'possible', and often the futuro: 'Sarà a casa'.",
                it: "L'inglese ha una scala di verbi modali per la certezza (must, may, might, can't). L'italiano usa dovere per 'quasi sicuro', potere o 'forse' per 'possibile', e spesso il futuro: 'Sarà a casa'.",
            },
            {
                en: "Forse is followed by the normal indicative: 'Forse è a casa'. With 'può darsi che' you need the congiuntivo: 'Può darsi che sia a casa'.",
                it: "Forse è seguito dal normale indicativo: 'Forse è a casa'. Con 'può darsi che' serve il congiuntivo: 'Può darsi che sia a casa'.",
            },
        ],
    },
    {
        id: 'combined-pronouns',
        level: 'B1',
        title: { en: 'Two pronouns together (me lo, glielo)', it: 'Due pronomi insieme (me lo, glielo)' },
        summary: {
            en: 'Saying "it to him" or "them for her" in one go.',
            it: 'Dire "a lui lo" o "per lei li" in una volta.',
        },
        forms: {
            english: [
                {
                    name: 'Pronoun + to / for + pronoun',
                    formula: {
                        en: 'The direct pronoun comes first, then to or for plus the person: I gave it to him. We bought them for her.',
                        it: 'Prima il pronome diretto, poi to o for e la persona: I gave it to him. We bought them for her.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Indirect + direct',
                    formula: {
                        en: 'The two pronouns join before the verb, indirect first. mi, ti, ci, vi become me, te, ce, ve. gli and le become glie- and join: glielo, gliela, glieli, gliele. After an infinitive or command, they attach: Dammelo!',
                        it: 'I due pronomi si uniscono prima del verbo, prima l\'indiretto. mi, ti, ci, vi diventano me, te, ce, ve. gli e le diventano glie- e si uniscono: glielo, gliela, glieli, gliele. Dopo un infinito o un comando si attaccano: Dammelo!',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I gave it to him.', tag: 'it + to him' },
                italian: { text: "Gliel'ho dato.", tag: "glielo (gli + lo)" },
            },
            {
                english: { text: 'Give it to me!', tag: 'it + to me' },
                italian: { text: 'Dammelo!', tag: 'dammi + lo = dammelo' },
            },
            {
                english: { text: 'Can you send it to me?', tag: 'it + to me' },
                italian: { text: 'Me lo puoi mandare?', tag: 'me lo' },
            },
            {
                english: { text: 'I will tell you.', tag: 'it understood' },
                italian: { text: 'Te lo dirò.', tag: 'te lo' },
            },
            {
                english: { text: 'We bought them for her.', tag: 'them + for her' },
                italian: { text: 'Gliele abbiamo comprate.', tag: 'gliele (gli + le)' },
            },
        ],
        notes: [
            {
                en: "The order in Italian is always indirect then direct (me lo, not lo me), and the pair goes before the verb. English puts the direct pronoun first and adds to or for.",
                it: "In italiano l'ordine è sempre indiretto poi diretto (me lo, non lo me) e la coppia va prima del verbo. L'inglese mette prima il pronome diretto e aggiunge to o for.",
            },
            {
                en: "'Gli' and 'le' (to him, to her) both become 'glie-' when combined, so 'glielo' can mean 'it to him' or 'it to her'. Context decides.",
                it: "'Gli' e 'le' (a lui, a lei) diventano entrambi 'glie-' quando si combinano, quindi 'glielo' può voler dire 'it to him' o 'it to her'. Decide il contesto.",
            },
            {
                en: "In the passato prossimo the participle agrees with the direct pronoun: 'Gliele abbiamo comprate' (le = feminine plural things).",
                it: "Nel passato prossimo il participio concorda con il pronome diretto: 'Gliele abbiamo comprate' (le = cose femminili plurali).",
            },
        ],
    },
    {
        id: 'causative',
        level: 'B1',
        title: { en: 'Make, let, have something done', it: 'Far fare, lasciare, farsi fare' },
        summary: {
            en: 'Getting someone else to do something, or letting them.',
            it: 'Far fare qualcosa a qualcuno, o lasciarglielo fare.',
        },
        forms: {
            english: [
                {
                    name: 'Make / let / have / get',
                    formula: {
                        en: 'make someone + base verb (She made me laugh). let someone + base verb (Let me see). have / get something + participle (I had my hair cut).',
                        it: 'make qualcuno + verbo base (She made me laugh). let qualcuno + verbo base (Let me see). have / get qualcosa + participio (I had my hair cut).',
                    },
                },
            ],
            italian: [
                {
                    name: 'Fare + infinito',
                    formula: {
                        en: 'fare + infinitive covers make, have and get done: Mi ha fatto ridere. Faccio dipingere la casa. farsi + infinitive for yourself: Si è fatta tagliare i capelli. lasciare + infinitive = let. But "let me see" is Fammi vedere.',
                        it: 'fare + infinito copre make, have e get done: Mi ha fatto ridere. Faccio dipingere la casa. farsi + infinito per sé: Si è fatta tagliare i capelli. lasciare + infinito = let. Ma "let me see" si dice Fammi vedere.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'She made me laugh.', tag: 'made + person + verb' },
                italian: { text: 'Mi ha fatto ridere.', tag: 'fare + infinitive' },
            },
            {
                english: { text: 'He let me go.', tag: 'let + person + verb' },
                italian: { text: 'Mi ha lasciato andare.', tag: 'lasciare + infinitive' },
            },
            {
                english: { text: 'She had her hair cut.', tag: 'had + thing + participle' },
                italian: { text: 'Si è fatta tagliare i capelli.', tag: 'farsi + infinitive' },
            },
            {
                english: { text: 'I am having the house painted.', tag: 'have + thing + participle' },
                italian: { text: 'Faccio dipingere la casa.', tag: 'fare + infinitive' },
            },
            {
                english: { text: 'Let me see!', tag: 'let me + verb' },
                italian: { text: 'Fammi vedere!', tag: 'fare + infinitive' },
            },
        ],
        notes: [
            {
                en: "Italian uses fare where English chooses between make, have and get. It also uses fare for 'let me see': 'Fammi vedere', not 'Lasciami vedere'.",
                it: "L'italiano usa fare dove l'inglese sceglie tra make, have e get. Usa fare anche per 'let me see': 'Fammi vedere', non 'Lasciami vedere'.",
            },
            {
                en: "The person doing the action is a pronoun object before fare: 'Mi ha fatto ridere' (She made me laugh), not 'Ha fatto io ridere'.",
                it: "La persona che compie l'azione è un pronome oggetto prima di fare: 'Mi ha fatto ridere', non 'Ha fatto io ridere'.",
            },
            {
                en: "For something done to you by someone else, Italian uses farsi + infinitive: 'Mi sono fatto/a tagliare i capelli'. English has no reflexive: 'I had my hair cut'.",
                it: "Per qualcosa che ti fa fare qualcun altro, l'italiano usa farsi + infinito: 'Mi sono fatto/a tagliare i capelli'. L'inglese non ha il riflessivo: 'I had my hair cut'.",
            },
        ],
    },
    {
        id: 'wishes',
        level: 'B1',
        title: { en: 'Wishes and regrets', it: 'Desideri e rimpianti' },
        summary: {
            en: 'Things you wish were different.',
            it: 'Cose che vorresti fossero diverse.',
        },
        forms: {
            english: [
                {
                    name: 'I wish + past',
                    formula: {
                        en: 'I wish + past tense for the present (I wish I had more time). I wish + past perfect for the past (I wish I had studied more). I wish + could (I wish I could come).',
                        it: 'I wish + passato per il presente (I wish I had more time). I wish + past perfect per il passato (I wish I had studied more). I wish + could (I wish I could come).',
                    },
                },
            ],
            italian: [
                {
                    name: 'Vorrei / magari + congiuntivo',
                    formula: {
                        en: 'Same subject: vorrei + infinitive (Vorrei avere più tempo). Different subject: vorrei che + congiuntivo imperfetto (Vorrei che tu fossi qui). A stronger wish: Magari + congiuntivo (Magari avessi studiato di più!).',
                        it: 'Stesso soggetto: vorrei + infinito (Vorrei avere più tempo). Soggetto diverso: vorrei che + congiuntivo imperfetto (Vorrei che tu fossi qui). Un desiderio più forte: Magari + congiuntivo (Magari avessi studiato di più!).',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'I wish I had more time.', tag: 'wish + past' },
                italian: { text: 'Vorrei avere più tempo.', tag: 'vorrei + infinitive' },
            },
            {
                english: { text: 'I wish I could come.', tag: 'wish + could' },
                italian: { text: 'Vorrei poter venire.', tag: 'vorrei + potere' },
            },
            {
                english: { text: 'I wish you were here.', tag: 'wish + past' },
                italian: { text: 'Vorrei che tu fossi qui.', tag: 'vorrei che + congiuntivo' },
            },
            {
                english: { text: 'I wish I had studied more.', tag: 'wish + past perfect' },
                italian: { text: 'Magari avessi studiato di più!', tag: 'magari + congiuntivo trapassato' },
            },
            {
                english: { text: 'If only I knew!', tag: 'If only + past' },
                italian: { text: 'Se solo lo sapessi!', tag: 'se solo + congiuntivo' },
            },
        ],
        notes: [
            {
                en: "English uses a past tense after 'wish' even for the present ('I wish I had'). Italian uses vorrei with the infinitive when the subject is the same, or 'vorrei che' with the congiuntivo imperfetto when it changes.",
                it: "L'inglese usa un passato dopo 'wish' anche per il presente ('I wish I had'). L'italiano usa vorrei + infinito quando il soggetto è lo stesso, oppure 'vorrei che' con il congiuntivo imperfetto quando cambia.",
            },
            {
                en: "'Magari' by itself can mean 'I wish!' or 'maybe'. With the congiuntivo it expresses a strong wish: 'Magari fosse vero!' (If only it were true!).",
                it: "'Magari' da solo può voler dire 'I wish!' o 'forse'. Con il congiuntivo esprime un desiderio forte: 'Magari fosse vero!'.",
            },
        ],
    },
    {
        id: 'impersonal-si',
        level: 'B1',
        title: { en: 'People in general (one, you, they)', it: 'Le persone in generale (si)' },
        summary: {
            en: 'Saying what people do or what is allowed, without saying who.',
            it: 'Dire cosa fanno le persone o cosa è permesso, senza dire chi.',
        },
        forms: {
            english: [
                {
                    name: 'You / one / people / they',
                    formula: {
                        en: 'English chooses a general subject: you, one, people or they. People eat late in Italy. You can\'t smoke here. They say it is true.',
                        it: 'L\'inglese sceglie un soggetto generico: you, one, people o they. People eat late in Italy. You can\'t smoke here. They say it is true.',
                    },
                },
            ],
            italian: [
                {
                    name: 'Si + third person',
                    formula: {
                        en: "si + verb in the third person singular: In Italia si mangia tardi. Qui non si può fumare. Come si dice in italiano? With essere and an adjective, the adjective is plural: Quando si è stanchi.",
                        it: "si + verbo alla terza persona singolare: In Italia si mangia tardi. Qui non si può fumare. Come si dice in italiano? Con essere e un aggettivo, l'aggettivo è plurale: Quando si è stanchi.",
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'In Italy people eat late.', tag: 'people + verb' },
                italian: { text: 'In Italia si mangia tardi.', tag: 'si + verb' },
            },
            {
                english: { text: "You can't smoke here.", tag: 'you + verb' },
                italian: { text: 'Qui non si può fumare.', tag: 'non si + verb' },
            },
            {
                english: { text: 'They say it is true.', tag: 'they say' },
                italian: { text: 'Si dice che sia vero.', tag: 'si dice che + congiuntivo' },
            },
            {
                english: { text: 'How do you say it in Italian?', tag: 'you' },
                italian: { text: 'Come si dice in italiano?', tag: 'si dice' },
            },
            {
                english: { text: 'When you are tired, you sleep badly.', tag: 'you' },
                italian: { text: 'Quando si è stanchi, si dorme male.', tag: 'si è + plural adjective' },
            },
        ],
        notes: [
            {
                en: "English has four choices (you, one, people, they). Italian has one: si. It is used for rules, habits and general statements, and it is very common.",
                it: "L'inglese ha quattro scelte (you, one, people, they). L'italiano ne ha una: si. Si usa per regole, abitudini e affermazioni generali, ed è molto comune.",
            },
            {
                en: "With essere and an adjective, the adjective is plural: 'Quando si è stanchi'. Do not write 'Quando si è stanco'.",
                it: "Con essere e un aggettivo, l'aggettivo è plurale: 'Quando si è stanchi'. Non scrivere 'Quando si è stanco'.",
            },
            {
                en: "Do not mix this up with the si passivante (Qui si vendono libri), which is covered under the passive. In both, the verb is third person, but in the passive it agrees with the thing.",
                it: "Non confonderlo con il si passivante (Qui si vendono libri), trattato con il passivo. In entrambi il verbo è alla terza persona, ma nel passivo concorda con la cosa.",
            },
        ],
    },
    {
        id: 'tags-indirect',
        level: 'B1',
        title: { en: 'Question tags and indirect questions', it: 'Richieste di conferma e domande indirette' },
        summary: {
            en: "Checking something (isn't it?) and asking politely (could you tell me where...).",
            it: 'Chiedere conferma (vero?) e chiedere con gentilezza (mi può dire dove...).',
        },
        forms: {
            english: [
                {
                    name: 'Question tags and statement order',
                    formula: {
                        en: "Tags repeat the auxiliary with the opposite meaning: It's cold, isn't it? You like coffee, don't you? Indirect questions use statement order, with no do: Could you tell me where the bank is?",
                        it: "I tag ripetono l'ausiliare con il significato opposto: It's cold, isn't it? You like coffee, don't you? Le domande indirette usano l'ordine della frase affermativa, senza do: Could you tell me where the bank is?",
                    },
                },
            ],
            italian: [
                {
                    name: 'Vero? / no? and the question word',
                    formula: {
                        en: 'One fixed word at the end: vero? no? giusto? non è vero? Indirect questions keep the question word and the normal order: Sai dove abita?',
                        it: 'Una parola fissa alla fine: vero? no? giusto? non è vero? Le domande indirette mantengono la parola interrogativa e l\'ordine normale: Sai dove abita?',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: "It's cold, isn't it?", tag: "Tag: isn't it" },
                italian: { text: 'Fa freddo, vero?', tag: 'vero?' },
            },
            {
                english: { text: "You like coffee, don't you?", tag: "Tag: don't you" },
                italian: { text: 'Ti piace il caffè, no?', tag: 'no?' },
            },
            {
                english: { text: 'Do you know where he lives?', tag: 'where he lives (statement order)' },
                italian: { text: 'Sai dove abita?', tag: 'dove + verb' },
            },
            {
                english: { text: 'Could you tell me where the bank is?', tag: 'where the bank is' },
                italian: { text: "Mi può dire dov'è la banca?", tag: "dov'è la banca" },
            },
        ],
        notes: [
            {
                en: "English tags change with the verb and the person (isn't it, don't you, aren't they). Italian has fixed words you can add to any sentence: vero? no? giusto?",
                it: "I tag inglesi cambiano con il verbo e la persona (isn't it, don't you, aren't they). L'italiano ha parole fisse che puoi aggiungere a qualsiasi frase: vero? no? giusto?",
            },
            {
                en: "An English indirect question uses statement order ('where he lives', not 'where does he live'). Italian never uses do, so the order is the normal one: 'dove abita'.",
                it: "Una domanda indiretta inglese usa l'ordine della frase affermativa ('where he lives', non 'where does he live'). L'italiano non usa mai do, quindi l'ordine è quello normale: 'dove abita'.",
            },
        ],
    },
    {
        id: 'emphasis',
        level: 'B1',
        title: { en: 'Emphasis (It is ... who)', it: 'Enfasi (È ... che)' },
        summary: {
            en: 'Putting the focus on one part of a sentence.',
            it: "Mettere l'accento su una parte della frase.",
        },
        forms: {
            english: [
                {
                    name: 'It is ... that / who',
                    formula: {
                        en: 'It + be + the focused part + who / that: It is John who called. It is here that I live. What I need is a coffee.',
                        it: 'It + be + la parte in evidenza + who / that: It is John who called. It is here that I live. What I need is a coffee.',
                    },
                },
            ],
            italian: [
                {
                    name: 'È ... che',
                    formula: {
                        en: 'È + focused part + che: È Gianni che ha chiamato. È qui che vivo. Quello che mi serve è un caffè. È stata Maria a chiamare. A stressed subject goes after the verb: L\'ho fatto io.',
                        it: 'È + parte in evidenza + che: È Gianni che ha chiamato. È qui che vivo. Quello che mi serve è un caffè. È stata Maria a chiamare. Un soggetto enfatico va dopo il verbo: L\'ho fatto io.',
                    },
                },
            ],
        },
        examples: [
            {
                english: { text: 'It is John who called.', tag: 'It is ... who' },
                italian: { text: 'È Gianni che ha chiamato.', tag: 'È ... che' },
            },
            {
                english: { text: 'It is here that I live.', tag: 'It is ... that' },
                italian: { text: 'È qui che vivo.', tag: 'È ... che' },
            },
            {
                english: { text: 'What I need is a coffee.', tag: 'What ... is' },
                italian: { text: 'Quello che mi serve è un caffè.', tag: 'Quello che ... è' },
            },
            {
                english: { text: 'It was Maria who called.', tag: 'It was ... who' },
                italian: { text: 'È stata Maria a chiamare.', tag: 'È stata ... a + infinitive' },
            },
            {
                english: { text: 'I did it myself.', tag: 'myself' },
                italian: { text: "L'ho fatto io.", tag: 'Stressed subject after the verb' },
            },
        ],
        notes: [
            {
                en: 'The two languages build emphasis in a very similar way: the focused part comes first, then che (Italian) or who / that (English).',
                it: "Le due lingue costruiscono l'enfasi in modo molto simile: prima la parte in evidenza, poi che (italiano) o who / that (inglese).",
            },
            {
                en: "With a person as the subject, Italian often prefers a + infinitive: 'È stata Maria a chiamare' instead of 'È Maria che ha chiamato'.",
                it: "Con una persona come soggetto, l'italiano spesso preferisce a + infinito: 'È stata Maria a chiamare' invece di 'È Maria che ha chiamato'.",
            },
            {
                en: "Italian can also stress the subject by putting the pronoun after the verb: 'L'ho fatto io' (I did it, not someone else). English stresses it with the voice or with 'myself'.",
                it: "L'italiano può anche enfatizzare il soggetto mettendo il pronome dopo il verbo: 'L'ho fatto io'. L'inglese lo enfatizza con la voce o con 'myself'.",
            },
        ],
    },
];